/**
 * What USPS calls a piece of mail, decided by its numbers alone.
 *
 * First-Class Mail has three shapes that are not parcels - letters, flats
 * (large envelopes) and cards - and USPS prices them by shape and weight, not by
 * zone. Whether something *is* a letter or a flat is purely a matter of its size
 * and weight, so it is worked out here once, in the shared package, rather than
 * in whichever place first needs to know.
 *
 * Inches and ounces throughout. The Mail page works in pounds; convert before
 * calling.
 */
/** USPS First-Class Mail letter: at most 11.5 x 6.125 x 0.25 in and 3.5 oz. */
export const LETTER_LIMITS = { long: 11.5, short: 6.125, thick: 0.25, maxOz: 3.5 };
/** USPS First-Class Mail flat (large envelope): at most 15 x 12 x 0.75 in and 13 oz. */
export const FLAT_LIMITS = { long: 15, short: 12, thick: 0.75, maxOz: 13 };
/**
 * Orientation is irrelevant to USPS - a 6 x 11 envelope is the same piece as an
 * 11 x 6 one - so the two flat dimensions are compared longest-to-longest and
 * the smallest dimension is taken as the thickness.
 */
function sortedDimensions({ lengthIn, widthIn, heightIn }) {
    const [thick, short, long] = [lengthIn, widthIn, heightIn].sort((a, b) => a - b);
    return [long, short, thick];
}
/** Floating point arithmetic should not turn a 3.5 oz letter into an over-limit one. */
const EPSILON = 1e-9;
export function classifyMail(measures) {
    const [long, short, thick] = sortedDimensions(measures);
    const { weightOz } = measures;
    if (long <= LETTER_LIMITS.long + EPSILON &&
        short <= LETTER_LIMITS.short + EPSILON &&
        thick <= LETTER_LIMITS.thick + EPSILON &&
        weightOz <= LETTER_LIMITS.maxOz + EPSILON) {
        return 'LETTER';
    }
    if (long <= FLAT_LIMITS.long + EPSILON &&
        short <= FLAT_LIMITS.short + EPSILON &&
        thick <= FLAT_LIMITS.thick + EPSILON &&
        weightOz <= FLAT_LIMITS.maxOz + EPSILON) {
        return 'FLAT';
    }
    return 'PACKAGE';
}
/**
 * Why a piece cannot go as the class someone asked for, or null if it can.
 *
 * Used to stop a request before the carrier does: "this is 14 oz, the limit for
 * a flat is 13" is something a person can act on, where the carrier's rejection
 * (or worse, a silent switch to a package price) is not.
 */
export function explainMailClass(requested, measures) {
    if (requested === 'PACKAGE')
        return null;
    const actual = classifyMail(measures);
    if (actual === requested || (requested === 'FLAT' && actual === 'LETTER'))
        return null;
    const limits = requested === 'LETTER' ? LETTER_LIMITS : FLAT_LIMITS;
    const [long, short, thick] = sortedDimensions(measures);
    const name = requested === 'LETTER' ? 'letter' : 'flat';
    const problems = [];
    if (measures.weightOz > limits.maxOz + EPSILON) {
        problems.push(`${format(measures.weightOz)} oz is over the ${format(limits.maxOz)} oz limit`);
    }
    if (long > limits.long + EPSILON || short > limits.short + EPSILON) {
        problems.push(`${format(long)} x ${format(short)} in is over the ${format(limits.long)} x ${format(limits.short)} in limit`);
    }
    if (thick > limits.thick + EPSILON) {
        problems.push(`${format(thick)} in thick is over the ${format(limits.thick)} in limit`);
    }
    return `Cannot be sent as a ${name}: ${problems.join('; ')}. It would be priced as a package.`;
}
function format(value) {
    return String(Math.round(value * 1000) / 1000);
}
//# sourceMappingURL=mail-class.js.map